import type { FC } from 'react';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { useFormik } from 'formik';
import type { IManualPaymentFormValues } from './useManualPaymentValidationSchema';
import { useManualPaymentValidationSchema } from './useManualPaymentValidationSchema';
import {
  type ResidentBalanceInfo,
  useResidentBalanceInfo,
} from '@pflegenavi/frontend/api-nursing-home';
import {
  type FamilyMemberResponse,
  useFMContext,
} from '@pflegenavi/frontend/family-member-context';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';
import {
  ManualPaymentStatus,
  useConfirmManualPayment,
} from '../../hooks/useConfirmManualPayment';
import { ManualPaymentFormModalView } from './ManualPaymentFormModalView';
import { Box, Card, Modal, ModalInner } from '@pflegenavi/web-components';
import { CircularProgress } from '@mui/material';

interface ManualPaymentFormModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  activeBalance: number | undefined;
  isActiveBalanceLoading: boolean;
}

export const ManualPaymentFormModal: FC<ManualPaymentFormModalProps> = ({
  open,
  setOpen,
  activeBalance,
  isActiveBalanceLoading,
}) => {
  const { selectedResidentId, familyMember } = useFMContext();

  const { data: balanceInfo, isPending: isBalanceInfoLoading } =
    useResidentBalanceInfo({
      residentId: selectedResidentId ?? '',
    });

  const handleCloseEscape = (
    _event: React.SyntheticEvent,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };

  const isLoading =
    isBalanceInfoLoading || isActiveBalanceLoading || !balanceInfo;

  return (
    <Modal open={open} handleClose={handleCloseEscape}>
      <ModalInner>
        {isLoading ? (
          <Box sx={{ width: 500 }}>
            <Card
              sx={{
                p: 3,
                height: 300,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress />
            </Card>
          </Box>
        ) : (
          <ManualPaymentFormModalContent
            setOpen={setOpen}
            activeBalance={activeBalance}
            balanceInfo={balanceInfo}
            familyMember={familyMember}
          />
        )}
      </ModalInner>
    </Modal>
  );
};

interface ManualPaymentFormModalContentProps {
  setOpen: (open: boolean) => void;
  activeBalance: number | undefined;
  balanceInfo: ResidentBalanceInfo;
  familyMember: FamilyMemberResponse | undefined;
}

const ManualPaymentFormModalContent: FC<ManualPaymentFormModalContentProps> = ({
  setOpen,
  activeBalance,
  balanceInfo,
  familyMember,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  const { manualPaymentStatus, confirm: confirmManualPayment } =
    useConfirmManualPayment();

  const [error, setError] = useState(false);

  const { initialValues, validationSchema } = useManualPaymentValidationSchema({
    familyMember,
    balanceInfo,
    residentBalance: activeBalance,
  });

  const handleSubmit = async (values: IManualPaymentFormValues) => {
    const { name, amount, iban, email } = values;
    const result = await confirmManualPayment({
      name,
      email,
      iban,
      amount,
    });
    if (result === ManualPaymentStatus.Success) {
      setOpen(false);
    }
    if (result === ManualPaymentStatus.Error) {
      setError(true);
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const { setFieldValue } = formik;

  const handleCloseButton = () => {
    setOpen(false);
  };

  const handleChangeAmount = useCallback(
    (value: number | undefined) => {
      setFieldValue('amount', value ? value : undefined);
    },
    [setFieldValue]
  );

  const translations = {
    newPayment: t('fm-triggered-payments.amount-screen.new-payment'),
    paymentFailed: t('fm-triggered-payments.error.payment-failed'),
    initiatorDetails: t('fm-triggered-payments.form-details.initiator-details'),
    fullName: t('mobile.family-member.full-name-field'),
    email: t('mobile.family-member.email-field'),
    pleaseEnterAmount: t(
      'fm-triggered-payments.amount-screen.please-enter-amount'
    ),
    topUpAmount: t('fm-triggered-payments.amount-screen.top-up-amount'),
    legalText: t('fm-triggered-payments.form-details.legal-text'),
    byClickingPay: t('fm-triggered-payments.form-details.by-clicking-pay'),
    cancel: t('actions.cancel'),
    payNow: t('fm-triggered-payments.pay-now'),
  };

  return (
    <ManualPaymentFormModalView
      onClose={handleCloseButton}
      activeBalance={activeBalance}
      isActiveBalanceLoading={false}
      isBalanceInfoLoading={false}
      error={error}
      manualPaymentStatus={manualPaymentStatus}
      formik={formik}
      onAmountChange={handleChangeAmount}
      onSubmit={async () => await formik.handleSubmit()}
      translations={translations}
      formatCurrency={(amount) =>
        amount !== undefined ? fCurrencyCents(amount) : ''
      }
    />
  );
};
